import React from "react";
import classNames from "classnames";
import Tippy from "@tippyjs/react";

import "./FollowerGallery.scss";
import { useTranslation } from "react-i18next";
import EmbellishedHeading from "../EmbellishedHeading/EmbellishedHeading.js";
import Stack from "../Stack/Stack.js";

const getFollowerType = (type) => (follower) => follower.type === type;

const ROMAN_NUMERALS_BY_DECIMAL = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
  5: "V",
  6: "VI",
  7: "VII",
  8: "VIII",
  9: "IX",
  10: "X",
  11: "XI",
  12: "XII",
  13: "XIII",
  14: "XIV",
  15: "XV",
  16: "XVI",
  17: "XVII",
  18: "XVIII",
  19: "XIX",
  20: "XX",
};

const Follower = ({
  skinName,
  colorOptionIndex = 0,
  name,
  stats,
  selectable,
  isSelected,
  onClick,
}) => {
  const variationPreviewUrl = `assets/followers/variation-previews/${skinName}-${colorOptionIndex}.png`;
  const classes = classNames("follower-gallery__follower-container", {
    "follower-gallery__follower-container--selected": isSelected,
    "follower-gallery__follower-container--selectable": selectable,
  });

  return (
    <div className={classes} onClick={selectable ? onClick : () => {}}>
      <img
        src={variationPreviewUrl}
        alt=""
        className="follower-gallery__follower-preview"
      />
      <div className="follower-gallery__follower-info">
        <div className="follower-gallery__follower-name">
          {name}
          {stats && ` - ${ROMAN_NUMERALS_BY_DECIMAL[stats.level]}`}
        </div>
        {stats?.reason_of_death && (
          <div className="follower-gallery__follower-death-info">
            † {stats.reason_of_death}
          </div>
        )}
      </div>
      {stats?.traits && (
        <ul className="follower-gallery__traits">
          {stats.traits.map((trait) => {
            const traitIconUrl = `assets/ui/icons/traits/Icon_Trait_${trait.id}.png`;
            return (
              <Tippy
                key={trait.id}
                className="follower-gallery__trait-tooltip"
                arrow={false}
                content={trait.name}
              >
                <li className="follower-gallery__trait">
                  <div
                    className={`follower-gallery__trait-icon follower-gallery__trait-icon--${trait.type}`}
                  >
                    <img src={traitIconUrl} alt="" />
                  </div>
                  {/*<span>{trait.name}</span>*/}
                </li>
              </Tippy>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const FollowerGallery = ({
  followers,
  selectedFollower,
  activeSelectionEvent,
  onSelectFollower,
}) => {
  const { t, i18n } = useTranslation();

  const selectionReason = activeSelectionEvent?.reason;
  const selectionEventPrompt =
    selectionReason &&
    (i18n.exists(
      `features.followerGallery.selection_event_prompt_by_reason.${selectionReason}`
    )
      ? t(
          `features.followerGallery.selection_event_prompt_by_reason.${selectionReason}`
        )
      : t("features.followerGallery.selection_event_prompt_fallback"));
  const canSelectFollower = Boolean(activeSelectionEvent && !selectedFollower);
  const viewerFollowers = followers.filter(getFollowerType("viewer"));
  const npcFollowers = followers.filter(getFollowerType("npc"));
  let followersToDisplay = [...viewerFollowers, ...npcFollowers].filter(
    (follower) => {
      if (
        !activeSelectionEvent ||
        activeSelectionEvent?.allowed_follower_ids === "*"
      ) {
        return true;
      }

      const idToCheck = (
        follower.type === "npc" ? follower.id : follower.viewer_id
      ).toString();
      return (activeSelectionEvent?.allowed_follower_ids || []).includes(
        idToCheck
      );
    }
  );

  const aliveFollowersToDisplay = followersToDisplay.filter(
    (follower) => !Boolean(follower.stats?.reason_of_death)
  );
  const deadFollowersToDisplay = followersToDisplay.filter((follower) =>
    Boolean(follower.stats?.reason_of_death)
  );

  followersToDisplay = [...aliveFollowersToDisplay, ...deadFollowersToDisplay];

  return (
    <Stack vertical className="follower-gallery">
      <EmbellishedHeading>
        {t("features.followerGallery.title")}
      </EmbellishedHeading>
      {selectionReason && <p>{selectionEventPrompt}</p>}
      {followersToDisplay.length === 0 && (
        <div className="follower-gallery__empty-state">
          <img src="assets/ui/SadLamb.png" alt="" />
          <p>{t("features.followerGallery.noFollowersText")}</p>
        </div>
      )}
      {followersToDisplay.length > 0 && (
        <div className="follower-gallery__followers">
          {followersToDisplay.map((follower) => (
            <Follower
              key={follower.id}
              skinName={follower.customisations.skin_name}
              colorOptionIndex={follower.customisations.color.colorOptionIndex}
              name={
                follower[
                  follower.type === "npc" ? "name" : "viewer_display_name"
                ]
              }
              stats={follower.stats}
              selectable={canSelectFollower}
              isSelected={
                selectedFollower?.id.toString() === follower.id.toString()
              }
              onClick={() => onSelectFollower(follower)}
            />
          ))}
        </div>
      )}
      {/*<table>*/}
      {/*  <thead>*/}
      {/*    <tr>*/}
      {/*      <th>Follower type</th>*/}
      {/*      <th>Follower ID (viewer/NPC ID)</th>*/}
      {/*      <th></th>*/}
      {/*    </tr>*/}
      {/*  </thead>*/}
      {/*  <tbody>*/}
      {/*    {viewerFollowers.map((follower) => (*/}
      {/*      <tr key={follower.id}>*/}
      {/*        <td>Viewer</td>*/}
      {/*        <td>{follower.id}</td>*/}
      {/*        <td>*/}
      {/*          <button*/}
      {/*            disabled={!canSelectFollower}*/}
      {/*            onClick={() => onSelectFollower(follower.id)}*/}
      {/*          >*/}
      {/*            Select*/}
      {/*          </button>*/}
      {/*        </td>*/}
      {/*      </tr>*/}
      {/*    ))}*/}
      {/*    {npcFollowers.map((follower) => (*/}
      {/*      <tr key={follower.id}>*/}
      {/*        <td>NPC</td>*/}
      {/*        <td>{follower.id}</td>*/}
      {/*        <td>*/}
      {/*          <button*/}
      {/*            disabled={!canSelectFollower}*/}
      {/*            onClick={() => onSelectFollower(follower.id)}*/}
      {/*          >*/}
      {/*            Select*/}
      {/*          </button>*/}
      {/*        </td>*/}
      {/*      </tr>*/}
      {/*    ))}*/}
      {/*  </tbody>*/}
      {/*</table>*/}
    </Stack>
  );
};

export default FollowerGallery;
